import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { queriesApi } from '../services/queriesService'

/**
 * Hook para obtener y procesar los datos de ToolPaymentControl de Contentful
 * Utiliza Tanstack Query para cache de 5 días y mejores prácticas
 *
 * @returns {Object} Objeto con loading, error y datos procesados
 */
export const useContentfulToolPaymentControl = () => {
	// Query con Tanstack Query
	const {
		data: rawData,
		isLoading,
		error,
		refetch,
		isStale,
		isRefetching
	} = useQuery({
		queryKey: ['contentful', 'tool-payment-control'] as const,
		queryFn: queriesApi.getToolPaymentControl,
		staleTime: 5 * 24 * 60 * 60 * 1000, // 5 días
		cacheTime: 5 * 24 * 60 * 60 * 1000, // 5 días
		retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
		refetchOnWindowFocus: false,
		refetchOnReconnect: true
	})

	// Manejar errores de manera moderna
	if (error) {
		console.error('Error al obtener datos de ToolPaymentControl desde Contentful:', error)
	}

	// Procesar datos usando memoización para evitar recalculos innecesarios
	const toolPaymentControlData = useMemo(() => {
		if (!rawData || isLoading || error) {
			return {
				sys: null,
				isVisible: false,
				tituloGeneral: false,
				titulo2: false,
				titulo3: false,
				beneficioCollection: []
			}
		}

		try {
			const data = rawData?.contenidosRetos
			return {
				sys: data?.sys || null,
				isVisible: data?.isVisible || false,
				tituloGeneral: data?.tituloGeneral || false,
				titulo2: data?.titulo2 || false,
				titulo3: data?.titulo3 || false,
				beneficioCollection: data?.beneficioCollection?.items || []
			}
		} catch (transformError) {
			console.error('Error al transformar datos de ToolPaymentControl:', transformError)
			return {
				sys: null,
				isVisible: false,
				tituloGeneral: false,
				titulo2: false,
				titulo3: false,
				beneficioCollection: []
			}
		}
	}, [rawData, isLoading, error])

	// Información adicional sobre el estado del cache
	const cacheInfo = useMemo(
		() => ({
			isStale,
			isRefetching,
			lastFetch: rawData ? new Date() : null,
			cacheExpiry: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000) // 5 días desde ahora
		}),
		[isStale, isRefetching, rawData]
	)

	return {
		// Datos principales (manteniendo compatibilidad con el hook original)
		loading: isLoading,
		error,
		data: rawData,
		...toolPaymentControlData,

		// Funcionalidades adicionales de Tanstack Query
		refetch,
		isStale,
		isRefetching,
		cacheInfo,

		// Datos raw por si se necesitan
		rawData
	}
}

export default useContentfulToolPaymentControl
